import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import moment from "moment";
import ContentContainer from "../../../templates/content-container/contentContainer";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import { PAGE_IDS } from "../../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../../app/data/common/queryKeys";
import { userSelector } from "../../../slices/user/userSlice";
import { useSelector } from "react-redux";
import { UserUtils } from "../../../app/data/user/userUtils";
import PaymentService from "../../../app/data/payment/paymentService";
import {
  PaymentAggregates,
  PaymentDetail,
  PaymentHealth,
} from "../../../app/data/payment/PaymentSession";
import PaymentDetailSidebar from "./paymentDetailSidebar";
import PaymentSearchSection from "./paymentSearchSection";
import PaymentReportSection from "./paymentReportSection";
import PaymentHealthSection from "./paymentHealthSection";
import TransactionFeeCalculator from "./transactionFeeCalculator";
import "./paymentAdminBrowser.scss";

const paymentService = PaymentService.getInstance();
const SEARCH_PAGE_SIZE = 50;

const defaultDateFrom = () => moment().subtract(10, "days").toString();
const defaultDateTo = () => moment().toString();

const toApiDate = (value: string): string | undefined => {
  if (!value) return undefined;
  return value.toApiDateFormat();
};

const numericFilter = (value: string): number | undefined =>
  /^\d+$/.test(value) ? Number(value) : undefined;

const errorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const blobErrorMessage = async (data: unknown): Promise<string | null> => {
  if (!(data instanceof Blob)) return null;
  try {
    const parsed = JSON.parse(await data.text());
    if (typeof parsed?.message === "string" && parsed.message) return parsed.message;
    const fieldMessage = parsed?.errors?.[0]?.message;
    if (typeof fieldMessage === "string" && fieldMessage) return fieldMessage;
  } catch {
    return null;
  }
  return null;
};

const PaymentAdminBrowser: React.FC = () => {
  const userState = useSelector(userSelector);
  const location = useLocation();
  const isAdmin = UserUtils.canAccessFinancePayments(userState.profile);
  const initialInvoiceFromQuery = (() => {
    const raw = new URLSearchParams(location.search).get("invoiceNumber");
    return raw && /^\d+$/.test(raw) ? raw : "";
  })();
  const [tab, setTab] = useState<"browser" | "report" | "health" | "fee">("browser");
  const [status, setStatus] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState(initialInvoiceFromQuery);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [provider, setProvider] = useState("");
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PaymentDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [reportAccount, setReportAccount] = useState("");
  const [reportProvider, setReportProvider] = useState("");
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [aggregates, setAggregates] = useState<PaymentAggregates | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const [healthHours, setHealthHours] = useState(24);
  const [health, setHealth] = useState<PaymentHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);

  const searchFilters = useMemo(
    () => ({
      accountNumber: numericFilter(accountNumber),
      invoiceNumber: numericFilter(invoiceNumber),
      status: status || undefined,
      paymentMethod: paymentMethod || undefined,
      provider: provider || undefined,
      dateFrom: toApiDate(dateFrom),
      dateTo: toApiDate(dateTo),
    }),
    [accountNumber, dateFrom, dateTo, invoiceNumber, paymentMethod, provider, status]
  );

  const searchQuery = useInfiniteQuery({
    queryKey: [QUERY_KEYS.PAYMENT_ADMIN_SEARCH, searchFilters],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await paymentService.adminSearchPayments({
        ...searchFilters,
        page: pageParam,
        size: SEARCH_PAGE_SIZE,
      });
      if (!res.ok() || !res.data) {
        throw new Error(
          typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to search payments"
        );
      }
      return res.data;
    },
    getNextPageParam: (lastPage) => {
      const next = (lastPage?.number ?? 0) + 1;
      if (!lastPage || next >= (lastPage.totalPages || 0)) return undefined;
      return next;
    },
    enabled: isAdmin && tab === "browser",
    refetchOnWindowFocus: false,
    retry: false,
  });

  const searchRows = useMemo(
    () => searchQuery.data?.pages.flatMap((page) => page.content || []) ?? [],
    [searchQuery.data]
  );

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    setReportError(null);
    const res = await paymentService.adminAggregates({
      accountNumber: reportAccount ? Number(reportAccount) : undefined,
      provider: reportProvider || undefined,
      dateFrom: reportFrom || undefined,
      dateTo: reportTo || undefined,
    });
    if (res.ok() && res.data) {
      setAggregates(res.data);
    } else {
      setAggregates(null);
      setReportError(
        typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to load payment report"
      );
    }
    setReportLoading(false);
  }, [reportAccount, reportFrom, reportProvider, reportTo]);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);
    const res = await paymentService.adminHealth(healthHours);
    if (res.ok() && res.data) {
      setHealth(res.data);
    } else {
      setHealth(null);
      setHealthError(
        typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to load payment health"
      );
    }
    setHealthLoading(false);
  }, [healthHours]);

  useEffect(() => {
    if (isAdmin && tab === "report") loadReport();
  }, [isAdmin, loadReport, tab]);

  useEffect(() => {
    if (isAdmin && tab === "health") loadHealth();
  }, [isAdmin, loadHealth, tab]);

  const closeDetail = () => {
    setSelectedPaymentId(null);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  const openDetail = async (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    const res = await paymentService.getPaymentDetail(paymentId);
    if (res.ok() && res.data) {
      setDetail(res.data);
    } else {
      setDetailError(
        typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to load detail"
      );
    }
    setDetailLoading(false);
  };

  const exportPaidInvoices = async () => {
    if (!dateFrom || !dateTo) return;
    setExporting(true);
    setExportError(null);
    try {
      const apiDateFrom = toApiDate(dateFrom);
      const apiDateTo = toApiDate(dateTo);
      if (!apiDateFrom || !apiDateTo) return;
      const res = await paymentService.exportPaidInvoicesCsv({
        accountNumber: numericFilter(accountNumber),
        invoiceNumber: numericFilter(invoiceNumber),
        paymentMethod: paymentMethod || undefined,
        provider: provider || undefined,
        dateFrom: apiDateFrom,
        dateTo: apiDateTo,
      });
      if (!res.ok() || !res.data) {
        const parsed = await blobErrorMessage(res.data);
        throw new Error(
          parsed ||
            (typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to export paid invoices")
        );
      }
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `paid-invoices-${apiDateFrom}-${apiDateTo}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(errorMessage(e, "Failed to export paid invoices"));
    } finally {
      setExporting(false);
    }
  };

  const exportReportCsv = () => {
    if (!aggregates) return;
    const lines = [
      "metric,value",
      `totalPayments,${aggregates.totalPayments}`,
      `completedCount,${aggregates.completedCount}`,
      `failedCount,${aggregates.failedCount}`,
      `pendingCount,${aggregates.pendingCount}`,
      `cancelledCount,${aggregates.cancelledCount}`,
      `expiredCount,${aggregates.expiredCount}`,
      `completedAmountCents,${aggregates.completedAmountCents}`,
      `completedSurchargeCents,${aggregates.completedSurchargeCents ?? 0}`,
      `completedTotalChargedCents,${aggregates.completedTotalChargedCents ?? aggregates.completedAmountCents}`,
      `successRate,${aggregates.successRate}`,
      `failRate,${aggregates.failRate}`,
      ...Object.entries(aggregates.countByProvider || {}).map(
        ([k, v]) => `providerCount_${k},${v}`
      ),
      ...(aggregates.completedAmountByProvider || []).flatMap((p) => [
        `completedAmount_${p.provider},${p.amountCents}`,
        `completedSurcharge_${p.provider},${p.surchargeCents ?? 0}`,
        `completedTotalCharged_${p.provider},${p.totalChargedCents ?? p.amountCents}`,
      ]),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payment-report-${reportFrom || "all"}-${reportTo || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return (
      <ContentContainer title="Payments">
        <XGSErrorMessage>Only XGS administrators and Finance Admins can view the payment browser.</XGSErrorMessage>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer title="Payment browser" isLoading={reportLoading || healthLoading}>
      <div className="xgs-payment-admin">
        <div className="xgs-payment-admin__tabs">
          <Button
            theme={tab === "browser" ? ButtonThemes.blue : ButtonThemes.gray}
            onClick={() => setTab("browser")}
          >
            Search
          </Button>
          <Button
            theme={tab === "report" ? ButtonThemes.blue : ButtonThemes.gray}
            onClick={() => setTab("report")}
          >
            Report
          </Button>
          <Button
            theme={tab === "health" ? ButtonThemes.blue : ButtonThemes.gray}
            onClick={() => setTab("health")}
          >
            Health
          </Button>
          <Button
            theme={tab === "fee" ? ButtonThemes.blue : ButtonThemes.gray}
            onClick={() => setTab("fee")}
          >
            Transaction fee
          </Button>
        </div>

        {tab === "browser" && (
          <PaymentSearchSection
            accountNumber={accountNumber}
            invoiceNumber={invoiceNumber}
            status={status}
            paymentMethod={paymentMethod}
            provider={provider}
            dateFrom={dateFrom}
            dateTo={dateTo}
            rows={searchRows}
            error={searchQuery.isError ? errorMessage(searchQuery.error, "Failed to search payments") : null}
            exportError={exportError}
            isLoading={searchQuery.isLoading || (searchQuery.isFetching && !searchQuery.isFetchingNextPage)}
            isExporting={exporting}
            infiniteScrollHasNext={!!searchQuery.hasNextPage}
            infiniteScrollLoading={searchQuery.isFetchingNextPage}
            onAccountChange={setAccountNumber}
            onInvoiceChange={setInvoiceNumber}
            onStatusChange={setStatus}
            onPaymentMethodChange={setPaymentMethod}
            onProviderChange={setProvider}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onSearch={() => {
              searchQuery.refetch();
            }}
            onExportCsv={exportPaidInvoices}
            onInfiniteScroll={() => {
              if (searchQuery.hasNextPage && !searchQuery.isFetchingNextPage) {
                searchQuery.fetchNextPage();
              }
            }}
            onRowClick={openDetail}
          />
        )}

        {tab === "report" && (
          <PaymentReportSection
            reportAccount={reportAccount}
            reportProvider={reportProvider}
            reportFrom={reportFrom}
            reportTo={reportTo}
            aggregates={aggregates}
            reportError={reportError}
            isLoading={reportLoading}
            onAccountChange={setReportAccount}
            onProviderChange={setReportProvider}
            onFromChange={setReportFrom}
            onToChange={setReportTo}
            onRunReport={() => loadReport()}
            onExportCsv={exportReportCsv}
          />
        )}

        {tab === "fee" && <TransactionFeeCalculator />}

        {tab === "health" && (
          <PaymentHealthSection
            healthHours={healthHours}
            health={health}
            healthError={healthError}
            isLoading={healthLoading}
            onHoursChange={setHealthHours}
            onRefresh={() => loadHealth()}
            onFailureClick={openDetail}
          />
        )}

        <PaymentDetailSidebar
          show={!!selectedPaymentId || !!detailError}
          isLoading={detailLoading}
          detail={detail}
          detailError={detailError}
          onClose={closeDetail}
        />

        <span className="xgs-payment-admin__page-id" data-page-id={PAGE_IDS.PAYMENT_ADMIN} />
      </div>
    </ContentContainer>
  );
};

export default PaymentAdminBrowser;
